import { useState } from 'react';
import clsx from 'clsx';
import { formatCurrency } from '../../lib/format';
import { legReparatieAkkoordVast, type AkkoordBron, type ReparatieOpdracht } from '../../lib/reparatie';

interface Props {
  opdracht: ReparatieOpdracht;
  onClose: () => void;
  onDone: () => void;
}

const BRONNEN: Array<{ value: AkkoordBron; label: string }> = [
  { value: 'e-mail', label: 'E-mail' },
  { value: 'telefoon', label: 'Telefoon' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'balie', label: 'Aan de balie / bij de chauffeur' },
];

/**
 * Klant-akkoord vastleggen namens de klant. Het bewijs is de kern: wat de
 * klant zei of schreef, met datum. Zonder bewijs geen akkoord (server
 * bewaakt dat ook) — een vinkje zonder bron is bij een geschil niets waard.
 */
export function ReparatieAkkoordDialog({ opdracht, onClose, onDone }: Props) {
  const [bron, setBron] = useState<AkkoordBron>('e-mail');
  const [bewijs, setBewijs] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const canSubmit = !busy && bewijs.trim().length >= 5;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await legReparatieAkkoordVast({ opdrachtId: opdracht.id, bron, bewijs: bewijs.trim() });
      if (!res.success) {
        setError(res.message || 'Vastleggen mislukt');
        setBusy(false);
        return;
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Vastleggen mislukt');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Klant-akkoord vastleggen</h2>
          <p className="text-xs text-slate-500 mt-1">
            {opdracht.opdrachtNummer}
            {opdracht.verkoopBedrag != null ? ` · ${formatCurrency(opdracht.verkoopBedrag)} incl. btw` : ''}
          </p>
        </div>

        {done ? (
          <div className="px-6 py-6 space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
              <p className="text-sm font-medium text-emerald-900">Akkoord vastgelegd</p>
              <p className="text-xs text-emerald-700 mt-1">
                De reparatie staat nu als regel op de order en gaat mee in de bevestigingsmails. De rit
                naar de reparateur wordt gepland zodra het kleed gewassen is.
              </p>
            </div>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={onDone}
                className="text-sm font-medium px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Sluiten
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-slate-700">
                Gebruik dit alleen als de klant al akkoord heeft gegeven buiten de klant-link om. Leg vast
                wat de klant zei of schreef, met datum. Dat is het bewijs bij een geschil.
              </p>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Hoe gaf de klant akkoord?</label>
                <select
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                  value={bron}
                  onChange={(e) => setBron(e.target.value as AkkoordBron)}
                  disabled={busy}
                >
                  {BRONNEN.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Bewijs (verplicht)</label>
                <textarea
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                  rows={3}
                  placeholder="Bijv. mail van 2 sept 10:14: 'Prima, ga akkoord met de €120 voor de franjes.'"
                  value={bewijs}
                  onChange={(e) => setBewijs(e.target.value)}
                  disabled={busy}
                />
              </div>
              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>
            <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={clsx(
                  'text-sm font-medium px-3 py-1.5 rounded',
                  canSubmit
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                )}
              >
                {busy ? 'Bezig…' : 'Akkoord vastleggen'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
